import Vue from 'vue';
import VueI18n from 'vue-i18n';
import * as messages from '../../public/assets/i18n/en.json';
import axios from 'axios';
import { activateDateFnsLocale } from './date-fns-locales';

Vue.use(VueI18n);

export interface ILanguageOption {
  id: string;
  name: string;
}

/** All supported UI languages with their native display names. */
export const SUPPORTED_LANGUAGES: ILanguageOption[] = [
  { id: 'en', name: 'English' },
  { id: 'es', name: 'Español' },
  { id: 'zh', name: '中文' },
  { id: 'hi', name: 'हिन्दी' },
  { id: 'fr', name: 'Français' },
  { id: 'ar', name: 'العربية' },
  { id: 'bn', name: 'বাংলা' },
  { id: 'pt', name: 'Português' },
  { id: 'ru', name: 'Русский' },
  { id: 'ur', name: 'اردو' },
  { id: 'id', name: 'Bahasa Indonesia' },
  { id: 'de', name: 'Deutsch' },
  { id: 'ja', name: '日本語' },
  { id: 'tr', name: 'Türkçe' },
  { id: 'vi', name: 'Tiếng Việt' },
  { id: 'it', name: 'Italiano' },
  { id: 'ca', name: 'Català' },
  { id: 'cs', name: 'Čeština' },
  { id: 'fi', name: 'Suomi' },
];

const translatedLanguages = SUPPORTED_LANGUAGES.map((lang) => lang.id);
const loadedLanguages = ['en']; // our default language that is preloaded

const LANG_STORAGE_KEY = 'cd-admin-lang';

export const i18n = new VueI18n({
  locale: 'en', // set locale
  fallbackLocale: 'en',
  // @ts-ignore
  messages: { en: messages.default }, // set locale messages
});

function setI18nLanguage(lang: string) {
  i18n.locale = lang;
  axios.defaults.headers.common['Accept-Language'] = lang;
  document.querySelector('html').setAttribute('lang', lang);
  try {
    localStorage.setItem(LANG_STORAGE_KEY, lang);
  } catch {
    // Storage unavailable (private mode / embedded webview); non-fatal.
  }
  void activateDateFnsLocale(lang);
  return lang;
}

export function loadLanguageAsync(lang: string) {
  // If the same language
  if (i18n.locale === lang) {
    return Promise.resolve(setI18nLanguage(lang));
  }

  // If the language was already loaded
  if (loadedLanguages.includes(lang)) {
    return Promise.resolve(setI18nLanguage(lang));
  }

  // If the language hasn't been loaded yet
  return import(
    /* webpackChunkName: "lang-[request]" */ `../../public/assets/i18n/${lang}.json`
  ).then(
    // tslint:disable-next-line:no-shadowed-variable
    (messages) => {
      i18n.setLocaleMessage(lang, messages.default);
      loadedLanguages.push(lang);
      return setI18nLanguage(lang);
    }
  );
}

const pendingLocaleLoads = new Map<string, Promise<void>>();

/**
 * Loads a locale's messages without switching the UI locale, so copy can be
 * translated into another language with `i18n.t(key, lang)`.
 */
export function ensureLocaleMessages(lang: string): Promise<void> {
  if (loadedLanguages.includes(lang)) return Promise.resolve();

  let pending = pendingLocaleLoads.get(lang);

  if (!pending) {
    pending = import(
      /* webpackChunkName: "lang-[request]" */ `../../public/assets/i18n/${lang}.json`
    )
      .then((messages) => {
        i18n.setLocaleMessage(lang, messages.default);
        if (!loadedLanguages.includes(lang)) loadedLanguages.push(lang);
      })
      .finally(() => {
        pendingLocaleLoads.delete(lang);
      });
    pendingLocaleLoads.set(lang, pending);
  }

  return pending;
}

/** Persisted choice wins; otherwise fall back to browser detection. */
function detectLanguage(): string {
  try {
    const stored = localStorage.getItem(LANG_STORAGE_KEY);
    if (stored && translatedLanguages.includes(stored)) return stored;
  } catch {
    // Storage unavailable; fall through to browser detection.
  }
  const browserLanguage =
    (!!navigator &&
      (navigator.language ||
        (navigator as Navigator & { userLanguage?: string }).userLanguage)) ||
    'en';
  const rootLang = browserLanguage.split('-')[0];
  if (translatedLanguages.includes(browserLanguage)) return browserLanguage;
  if (translatedLanguages.includes(rootLang)) return rootLang;
  return 'en';
}

const userLang = detectLanguage();

/**
 * Settles once the stored (or detected) language is active, or failed to
 * load (the UI then stays in English). Never rejects.
 */
export const initialLanguageLoaded: Promise<void> = (userLang !== 'en'
  ? loadLanguageAsync(userLang)
  : Promise.resolve(setI18nLanguage('en'))
).then(
  () => undefined,
  (error: unknown) => {
    console.error(`[i18n] Could not load the ${userLang} messages`, error);
  }
);
